import React, { useEffect, useRef } from 'react';
import type { Table, Order, Reservation } from '../../types';
import { getTableSeatsUsed, getOrderTotals, isFloorPlanActiveTableOrder, isActiveBuffetSession } from '../../lib/orderUtils';
import { reservationHoldsTable, isGeneralReservation } from '../../lib/reservationCapacity';
import ClockIcon from '../icons/ClockIcon';
import UsersIcon from '../icons/UsersIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import XCircleIcon from '../icons/XCircleIcon';
import BuffetTimer from './BuffetTimer';

interface TableDetailsModalProps {
  table: Table;
  orders: Order[];
  /** Floor tables — used to resolve joined reservation table names */
  tables?: Table[];
  reservations?: Reservation[];
  onClose: () => void;
  /** When true, only show reservations and guest status for the day (no orders/seat actions) */
  reservationOnly?: boolean;
  /** When set (e.g. from Reservations page), filter reservations to this date only */
  reservationDate?: string;
  onSeatNewParty?: (
    table: Table,
    context?: { partySize?: number; customerName?: string },
  ) => void;
  onSelectOrder?: (order: Order) => void;
  onEndBuffetSession?: (order: Order) => void;
  /** Mark reservation seated (Arrived) from POS floor */
  onUpdateReservation?: (id: number, updates: Partial<Reservation>) => Promise<void> | void;
  /** Transfer reservation to another table (no order yet) */
  onTransferReservation?: (reservation: Reservation, sourceTable: Table) => void;
}

const TableDetailsModal: React.FC<TableDetailsModalProps> = ({
  table,
  orders,
  tables = [],
  reservations = [],
  onClose,
  reservationOnly = false,
  reservationDate,
  onSeatNewParty,
  onSelectOrder,
  onEndBuffetSession,
  onUpdateReservation,
  onTransferReservation,
}) => {
  const today = new Date().toISOString().split('T')[0];
  // Active orders at this table: dine-in unpaid + buffet (session active)
  const tableOrders = orders.filter((o) => isFloorPlanActiveTableOrder(o, table.id));
  const seatsUsed = getTableSeatsUsed(tableOrders);
  const spareSeats = Math.max(0, table.capacity - seatsUsed);

  // Calculate totals from line items so we never show a stored totalAmount that outran items[].
  const totalAmount = tableOrders.reduce(
    (sum, order) => sum + getOrderTotals(order).totalAmount,
    0,
  );
  const totalItems = tableOrders.reduce((sum, order) => 
    sum + order.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0
  );

  const tableReservationsToday = reservations
    .filter((r) => {
      if (!reservationHoldsTable(r, table.id)) return false;
      if (r.status === 'cancelled' || r.status === 'completed' || r.status === 'waitlist') return false;
      if (reservationDate) return r.reservationDate === reservationDate;
      // POS floor: today's pending/confirmed/seated (show even slightly outside amber window)
      return r.reservationDate === today && (r.status === 'pending' || r.status === 'confirmed' || r.status === 'seated');
    })
    .sort((a, b) => {
      const dateCmp = a.reservationDate.localeCompare(b.reservationDate);
      if (dateCmp !== 0) return dateCmp;
      return a.reservationTime.localeCompare(b.reservationTime);
    });

  // Prefer general bookings for POS seating actions when both exist
  const primaryReservation =
    tableReservationsToday.find((r) => isGeneralReservation(r) && (r.status === 'pending' || r.status === 'confirmed'))
    ?? tableReservationsToday.find((r) => r.status === 'pending' || r.status === 'confirmed')
    ?? tableReservationsToday.find((r) => r.status === 'seated')
    ?? tableReservationsToday[0];

  const upcomingReservations = reservationOnly
    ? reservations
        .filter((r) => {
          if (!reservationHoldsTable(r, table.id) || (r.status !== 'pending' && r.status !== 'confirmed' && r.status !== 'seated')) return false;
          if (reservationDate) return r.reservationDate === reservationDate;
          return r.reservationDate >= today;
        })
        .sort((a, b) => {
          const dateCmp = a.reservationDate.localeCompare(b.reservationDate);
          if (dateCmp !== 0) return dateCmp;
          return a.reservationTime.localeCompare(b.reservationTime);
        })
    : tableReservationsToday;

  // Format elapsed time (mm:ss)
  const formatElapsedTime = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format "Arrived at" time
  const formatArrivedAt = (timestamp: number) => {
    const d = new Date(timestamp);
    return d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Get order status badge
  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      'new': 'bg-blue-100 text-blue-700 border-blue-300',
      'preparing': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'ready': 'bg-green-100 text-green-700 border-green-300',
      'served': 'bg-purple-100 text-purple-700 border-purple-300',
      'buffet': 'bg-purple-100 text-purple-700 border-purple-300',
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const handleSeatCustomer = async (r: Reservation) => {
    if (!onUpdateReservation) return;
    try {
      await onUpdateReservation(r.id, { status: 'seated' });
      // After seat (+ auto-merge), go straight into taking the order — no extra floor buttons.
      if (onSeatNewParty) {
        const primaryId =
          r.assignedTableIds && r.assignedTableIds.length > 0
            ? Number(r.assignedTableIds[0])
            : r.tableId != null
              ? Number(r.tableId)
              : Number(table.id);
        const primaryTable =
          tables.find((t) => Number(t.id) === primaryId) ?? table;
        onClose();
        onSeatNewParty(primaryTable, {
          partySize: Math.max(1, r.partySize || 1),
          customerName: r.customerName?.trim() || undefined,
        });
      }
    } catch {
      // Parent alerts
    }
  };

  const handleUndoArrival = async (r: Reservation) => {
    if (!onUpdateReservation) return;
    try {
      await onUpdateReservation(r.id, { status: 'confirmed' });
    } catch {
      // Parent alerts
    }
  };

  const seatedReservation = upcomingReservations.find((r) => r.status === 'seated');
  const joinRepairAttemptedRef = useRef<number | null>(null);

  // If a join booking is already seated but tables were never merged, repair once on open.
  useEffect(() => {
    if (reservationOnly || !onUpdateReservation || !seatedReservation) return;
    const ids = (seatedReservation.assignedTableIds ?? []).map(Number).filter((n) => n > 0);
    if (ids.length < 2) return;
    const mergedOk =
      Array.isArray(table.mergedWith) &&
      table.mergedWith.length >= 2 &&
      ids.every((id) => table.mergedWith!.some((m) => Number(m) === id));
    if (mergedOk) return;
    if (joinRepairAttemptedRef.current === seatedReservation.id) return;
    joinRepairAttemptedRef.current = seatedReservation.id;
    void onUpdateReservation(seatedReservation.id, { status: 'seated' });
  }, [
    reservationOnly,
    onUpdateReservation,
    seatedReservation,
    table.mergedWith,
  ]);

  const tableTitle = /^table\b/i.test(String(table.name || '').trim())
    ? String(table.name).trim()
    : `Table ${table.name}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-pink-500 to-pink-600 text-white">
          <div>
            <h2 className="text-xl font-bold">{tableTitle}</h2>
            <p className="text-sm text-pink-100">
              {reservationOnly ? `Capacity: ${table.capacity}` : (
                <>Capacity: {table.capacity} • {seatsUsed} seat{seatsUsed !== 1 ? 's' : ''} used
                {spareSeats > 0 && <> • <span className="text-green-200">{spareSeats} spare</span></>}</>
              )}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="text-white hover:text-pink-200 transition-colors"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Reservation block — primary seating path from amber floor tables */}
          {upcomingReservations.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-3">
                {reservationOnly ? 'Reservations for this table' : 'Reservation'}
              </h3>
              <ul className="space-y-3">
                {upcomingReservations.map((r) => {
                  const canSeat =
                    !reservationOnly &&
                    onUpdateReservation &&
                    (r.status === 'pending' || r.status === 'confirmed');
                  const canUndo =
                    !reservationOnly &&
                    onUpdateReservation &&
                    r.status === 'seated';
                  const canTransferReservation =
                    !reservationOnly &&
                    onTransferReservation &&
                    tableOrders.length === 0 &&
                    (r.status === 'pending' || r.status === 'confirmed' || r.status === 'seated');
                  const isPrimary = primaryReservation?.id === r.id;
                  return (
                    <li
                      key={r.id}
                      className={`rounded-lg border p-3 ${isPrimary ? 'border-amber-400 bg-white' : 'border-amber-100 bg-amber-50/50'}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-bold text-gray-900">{r.customerName}</p>
                          <p className="text-sm text-gray-700">
                            {r.reservationDate === today ? 'Today' : r.reservationDate} at {r.reservationTime}
                            {' · '}Party of {r.partySize}
                            {r.requiresMerge && r.assignedTableIds && r.assignedTableIds.length > 1 ? (
                              <span className="block text-xs text-amber-800 mt-0.5">
                                Join tables:{' '}
                                {r.assignedTableIds
                                  .map((id) => tables.find((t) => Number(t.id) === Number(id))?.name ?? `Table ${id}`)
                                  .join(' + ')}
                              </span>
                            ) : null}
                          </p>
                          {r.customerPhone && (
                            <p className="text-xs text-gray-500 mt-0.5">{r.customerPhone}</p>
                          )}
                          {(r.notes || r.specialRequests) && (
                            <p className="text-xs text-gray-600 mt-1">{r.notes || r.specialRequests}</p>
                          )}
                        </div>
                        <span className="text-xs uppercase font-bold tracking-wide text-amber-800 bg-amber-100 px-2 py-1 rounded">
                          {r.status}
                        </span>
                      </div>
                      {canSeat && (
                        <button
                          type="button"
                          onClick={() => void handleSeatCustomer(r)}
                          className="mt-3 w-full py-2.5 px-3 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                        >
                          Seat customer
                        </button>
                      )}
                      {canTransferReservation && (
                        <button
                          type="button"
                          onClick={() => onTransferReservation(r, table)}
                          className={`w-full py-2 px-3 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors ${canSeat || canUndo ? 'mt-2' : 'mt-3'}`}
                        >
                          Transfer table
                        </button>
                      )}
                      {canUndo && (
                        <button
                          type="button"
                          onClick={() => void handleUndoArrival(r)}
                          className="mt-2 w-full py-2 px-3 bg-white text-amber-800 text-sm font-semibold rounded-lg border border-amber-300 hover:bg-amber-50 transition-colors"
                        >
                          Undo arrival
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {reservationOnly && (
            <>
              {upcomingReservations.length === 0 && (
                <p className="text-gray-500 text-sm mb-4">No reservations for this table.</p>
              )}
              <div className="flex justify-end">
                <button onClick={onClose} className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-semibold">
                  Close
                </button>
              </div>
            </>
          )}

          {!reservationOnly && tableOrders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>
                {seatedReservation
                  ? `Guest seated (${seatedReservation.customerName}, party of ${seatedReservation.partySize}). Start an order when ready.`
                  : upcomingReservations.length > 0
                    ? 'No active orders yet — seat the guest above, then start an order.'
                    : 'No active orders for this table'}
              </p>
            </div>
          ) : !reservationOnly ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Items</p>
                    <p className="text-2xl font-bold text-gray-800">{totalItems}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-2xl font-bold text-pink-600">${totalAmount.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Customer Orders */}
              {tableOrders.map((order, orderIndex) => {
                const customerLabel = order.customerName || `Customer ${order.customerNumber || orderIndex + 1}`;
                const elapsedTime = formatElapsedTime(order.timestamp);
                const pendingItems = order.items.filter(item => 
                  item.status === 'pending' && 
                  !(item.price < 0 || item.id.toString().includes('combo-discount'))
                );
                const completedItems = order.items.filter(item => 
                  item.status === 'completed' && 
                  !(item.price < 0 || item.id.toString().includes('combo-discount'))
                );
                const servedItems = order.items.filter(item => 
                  item.served === true && 
                  !(item.price < 0 || item.id.toString().includes('combo-discount'))
                );

                const isBuffet = order.orderType === 'buffet';
                
                return (
                  <div key={order.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                    {/* Customer Header */}
                    <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-200">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <UsersIcon className="w-4 h-4 text-pink-600" />
                          <h3 className="font-bold text-lg text-gray-800">
                            {isBuffet 
                              ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${customerLabel}`
                              : customerLabel}
                          </h3>
                          {isBuffet && (
                            <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              BUFFET
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1" title="Arrived">
                            <ClockIcon className="w-3 h-3" />
                            Arrived {formatArrivedAt(order.timestamp)}
                          </span>
                          <span className="flex items-center gap-1" title="Elapsed">
                            Elapsed {elapsedTime}
                          </span>
                          {isBuffet && order.buffetEndTime && (
                            <span className="flex items-center gap-1">
                              <BuffetTimer endTime={order.buffetEndTime} />
                              <span className="text-xs text-purple-600">(time remaining)</span>
                            </span>
                          )}
                          {order.servingStaffName && (
                            <span className="text-xs text-gray-500">Served by {order.servingStaffName}</span>
                          )}
                          <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusBadge(order.status)}`}>
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Order Total</p>
                        <p className="text-lg font-bold text-pink-600">${getOrderTotals(order).totalAmount.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Buffet: time left + End early (no Pending/Ready/Served) */}
                    {isBuffet && (
                      <div className="mb-3 space-y-3">
                        {order.buffetEndTime && (
                          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <p className="text-sm font-semibold text-purple-800 mb-2">Time left</p>
                            <div className="text-2xl font-bold text-purple-700">
                              <BuffetTimer endTime={order.buffetEndTime} className="text-lg" />
                            </div>
                          </div>
                        )}
                        {onEndBuffetSession && isActiveBuffetSession(order) && (
                          <button
                            type="button"
                            onClick={() => { onClose(); onEndBuffetSession(order); }}
                            className="w-full py-2.5 px-3 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            End session early
                          </button>
                        )}
                      </div>
                    )}

                    {/* Dine-in: Order Items Summary (Pending / Ready / Served) */}
                    {!isBuffet && (
                      <>
                        <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                          <div className="bg-blue-50 rounded p-2 text-center border border-blue-200">
                            <p className="text-blue-700 font-semibold">{pendingItems.length}</p>
                            <p className="text-blue-600">Pending</p>
                          </div>
                          <div className="bg-yellow-50 rounded p-2 text-center border border-yellow-200">
                            <p className="text-yellow-700 font-semibold">{completedItems.length}</p>
                            <p className="text-yellow-600">Ready</p>
                          </div>
                          <div className="bg-green-50 rounded p-2 text-center border border-green-200">
                            <p className="text-green-700 font-semibold">{servedItems.length}</p>
                            <p className="text-green-600">Served</p>
                          </div>
                        </div>

                        {/* Items List */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Items ({order.items.filter(item => !(item.price < 0 || item.id.toString().includes('combo-discount'))).length}):</p>
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {order.items
                              .filter(item => !(item.price < 0 || item.id.toString().includes('combo-discount')))
                              .map((item, itemIndex) => (
                                <div 
                                  key={`${item.id}-${itemIndex}`}
                                  className="flex items-start justify-between text-sm p-2 bg-gray-50 rounded border border-gray-100"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-gray-800">{item.quantity}x</span>
                                      <span className="text-gray-700">{item.name}</span>
                                      {item.isRemake && (
                                        <span className="text-xs font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded border border-purple-300">
                                          REMAKE
                                        </span>
                                      )}
                                    </div>
                                    {item.selectedMeatOption && (
                                      <p className="text-xs text-pink-600 mt-0.5 ml-6">Meat: {item.selectedMeatOption}</p>
                                    )}
                                    {item.notes && (
                                      <p className="text-xs text-orange-600 mt-0.5 ml-6 font-medium">Note: {item.notes}</p>
                                    )}
                                    {item.isRemake && item.remakeReason && (
                                      <p className="text-xs text-purple-600 mt-0.5 ml-6">Reason: {item.remakeReason}</p>
                                    )}
                                  </div>
                                  <div className="text-right ml-2">
                                    <p className="font-semibold text-gray-800">${(item.price * item.quantity).toFixed(2)}</p>
                                    <div className="flex items-center gap-1 mt-1">
                                      {item.status === 'completed' && (
                                        <CheckCircleIcon className="w-3 h-3 text-green-600" />
                                      )}
                                      {item.served && (
                                        <span className="text-xs text-green-600 font-semibold">Served</span>
                                      )}
                                      {item.status === 'pending' && (
                                        <span className="text-xs text-blue-600 font-semibold">Pending</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Order Notes */}
                    {order.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Order Notes:</p>
                        <p className="text-sm text-gray-600 bg-yellow-50 p-2 rounded border border-yellow-200">
                          {order.notes}
                        </p>
                      </div>
                    )}

                    {/* View / Edit order */}
                    {onSelectOrder && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => onSelectOrder(order)}
                          data-testid="view-edit-order"
                          className="w-full py-2 px-3 bg-pink-500 text-white text-sm font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                        >
                          View / Edit order
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Footer – only when not reservation-only */}
        {!reservationOnly && (
          <div className="p-4 border-t bg-gray-50 flex flex-wrap justify-between items-center gap-2">
            <div>
              {seatedReservation && onSeatNewParty ? (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onSeatNewParty(table, {
                      partySize: Math.max(1, seatedReservation.partySize || 1),
                      customerName: seatedReservation.customerName?.trim() || undefined,
                    });
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
                >
                  Start order — {seatedReservation.customerName} (party {seatedReservation.partySize})
                </button>
              ) : spareSeats > 0 && onSeatNewParty ? (
                <button
                  type="button"
                  onClick={() => { onClose(); onSeatNewParty(table); }}
                  data-testid="seat-new-party"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
                >
                  Seat new party ({spareSeats} seat{spareSeats !== 1 ? 's' : ''} available)
                </button>
              ) : null}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-semibold"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableDetailsModal;

